'use client';

// module imports
import { useCallback, useRef, useState } from 'react';

// file imports
import axiosInstance from '@/lib/axios';
import { showError, showSuccess } from '@/lib/toast';

// Types
interface FileUploadProps {
  onUploadSuccess: (url: string, fileName: string) => void;
  onUploadError?: (error: string) => void;
  folder?: string;
  makePublic?: boolean;
  accept?: string;
  maxSize?: number; // in MB
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
}

interface UploadProgress {
  isUploading: boolean;
  progress: number;
  fileName?: string;
}

export default function FileUpload({
  onUploadSuccess,
  onUploadError,
  folder = 'uploads',
  makePublic = true,
  accept = '*/*',
  maxSize = 10, // 10MB default
  className = '',
  children,
  disabled = false,
}: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    isUploading: false,
    progress: 0,
  });

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file size
      if (file.size > maxSize * 1024 * 1024) {
        return `File size must be less than ${maxSize}MB`;
      }

      // Check file type if accept is specified
      if (accept !== '*/*') {
        const acceptedTypes = accept.split(',').map((type) => type.trim());
        const fileType = file.type;
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

        const isAccepted = acceptedTypes.some((type) => type === fileType || type === fileExtension || (type.endsWith('/*') && fileType.startsWith(type.replace('/*', ''))));

        if (!isAccepted) {
          return `File type not supported. Accepted types: ${accept}`;
        }
      }

      return null;
    },
    [maxSize, accept]
  );

  const handleFileUpload = useCallback(
    async (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        showError(validationError);
        onUploadError?.(validationError);
        return;
      }

      setUploadProgress({
        isUploading: true,
        progress: 0,
        fileName: file.name,
      });

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);
        formData.append('makePublic', makePublic.toString());

        const response = await axiosInstance.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress((prev) => ({ ...prev, progress }));
            }
          },
        });

        if (response.data.success) {
          showSuccess('File uploaded successfully!');
          onUploadSuccess(response.data.data.url, response.data.data.fileName);
        } else {
          throw new Error(response.data.error || 'Upload failed');
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Upload failed';
        showError(errorMessage);
        onUploadError?.(errorMessage);
      } finally {
        setUploadProgress({
          isUploading: false,
          progress: 0,
        });
      }
    },
    [folder, makePublic, validateFile, onUploadSuccess, onUploadError]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileUpload(file);
      }
      // Reset input value to allow same file selection
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleFileUpload]
  );

  const handleClick = useCallback(() => {
    if (!disabled && !uploadProgress.isUploading) {
      fileInputRef.current?.click();
    }
  }, [disabled, uploadProgress.isUploading]);

  return (
    <div className={className}>
      <input ref={fileInputRef} type="file" accept={accept} onChange={handleFileChange} className="hidden" disabled={disabled || uploadProgress.isUploading} />

      <div onClick={handleClick} className="cursor-pointer">
        {children || (
          <div
            className={`
            border-2 border-dashed border-gray-300 rounded-lg p-6 text-center
            hover:border-gray-400 transition-colors
            ${disabled || uploadProgress.isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
          >
            {uploadProgress.isUploading ? (
              <div className="space-y-2">
                <div className="text-sm text-gray-600">Uploading {uploadProgress.fileName}...</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress.progress}%` }} />
                </div>
                <div className="text-xs text-gray-500">{uploadProgress.progress}%</div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-gray-600">Click to upload or drag and drop</div>
                <div className="text-sm text-gray-500">Max size: {maxSize}MB</div>
                {accept !== '*/*' && <div className="text-xs text-gray-400">Accepted: {accept}</div>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
